import type { TaskView } from '@/lib/api/generated/model';

/**
 * How many tasks carry each label, keyed by label identifier.
 *
 * Derived in one pass from the aggregate the workspace already holds. No
 * endpoint provides this count and none should: it is a projection of data in
 * hand, and its only consumer is the delete confirmation, which has to be able
 * to say "this will be removed from 12 tasks" before anything happens.
 *
 * Labels no task carries are absent rather than zero — the caller reads the
 * registry for the full vocabulary and looks each one up here.
 */
export function countLabelUsage(
  tasks: readonly TaskView[],
): ReadonlyMap<string, number> {
  const usage = new Map<string, number>();
  for (const task of tasks) {
    for (const label of task.labels ?? []) {
      usage.set(label.id, (usage.get(label.id) ?? 0) + 1);
    }
  }
  return usage;
}

/**
 * The vocabulary ordered for curation: most-used first, unused last, name as
 * the tiebreak.
 *
 * Contract order is arbitrary to someone maintaining a vocabulary. What they
 * came to find sits at the two ends — the words everyone is using, and the ones
 * nobody ever did, the second being exactly what a management surface exists to
 * let them remove. Sorting puts both within reach without adding a control.
 *
 * Extracted from the component because it is derived logic that decides what a
 * reader sees first, and a stable tiebreak is the sort of thing that silently
 * stops being stable.
 */
export function orderLabelsForCuration<T extends { id: string; name: string }>(
  labels: readonly T[],
  usage: ReadonlyMap<string, number>,
): T[] {
  return [...labels].sort((a, b) => {
    const byUse = (usage.get(b.id) ?? 0) - (usage.get(a.id) ?? 0);
    return byUse !== 0 ? byUse : a.name.localeCompare(b.name);
  });
}
